import React, { useState } from "react";

import { AlertTriangle } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";

// Freeform target statuses a superadmin can move an order to, ordered by the
// normal workflow. Labels mirror the kanban / status history timeline. Must
// stay in sync with the whitelist in change_sales_request_status (DB).
//
// "facturado" is a virtual status: it isn't a real sales_request.status value.
// The RPC maps it to status = 'in_transit' with assigned_transportista cleared
// to NULL, and records 'facturado' in the history.
//
// "dispatch_ready" (Listo para Despacho) is intentionally NOT offered: it's a
// legacy status with no kanban column, so moving an order into it strands the
// order off the operations board. It stays out of the RPC allowlist too.
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "pending_pricing", label: "Autorización por Precio" },
  { value: "pending", label: "Autorización Cobranza" },
  { value: "pending_credit", label: "Crédito Insuficiente" },
  { value: "prepaid", label: "Anticipado Pdte Pago" },
  { value: "payment_to_check", label: "Validación de Pago" },
  { value: "accepted", label: "Aprobada" },
  { value: "por_facturar", label: "Por Facturar" },
  { value: "facturado", label: "Facturado" },
  { value: "in_transit", label: "En Tránsito" },
  { value: "delivered", label: "Entregado" },
  { value: "rejected", label: "Rechazada" },
  { value: "test", label: "Prueba" },
];

interface ManualStatusChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesRequestId: string;
  currentStatus: string;
  onChanged?: () => void;
}

export const ManualStatusChangeDialog: React.FC<
  ManualStatusChangeDialogProps
> = ({ open, onOpenChange, salesRequestId, currentStatus, onChanged }) => {
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setNewStatus("");
    setReason("");
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!newStatus || !reason.trim()) return;
    setSubmitting(true);
    try {
      // The change_sales_request_status RPC enforces superadmin + reason and
      // relies on the status-change trigger for the audit row (who/when).
      const { error } = await supabase.rpc("change_sales_request_status", {
        p_sales_request_id: salesRequestId,
        p_new_status: newStatus,
        p_reason: reason.trim(),
      });

      if (error) throw error;

      toast({
        title: "Estado actualizado",
        description: "El estado de la nota de venta fue cambiado manualmente.",
      });
      reset();
      onOpenChange(false);
      onChanged?.();
    } catch (error: any) {
      console.error("Error changing status manually:", error);
      toast({
        title: "Error",
        description:
          error?.message ||
          "No se pudo cambiar el estado de la nota de venta.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const options = STATUS_OPTIONS.filter((o) => o.value !== currentStatus);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar estado manualmente</DialogTitle>
          <DialogDescription>
            Mueve la nota de venta a cualquier estado. Acción exclusiva de
            superadmin y registrada en el historial.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="manual-new-status">Nuevo estado</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger id="manual-new-status" className="h-9">
                <SelectValue placeholder="Selecciona un estado" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manual-status-reason">Motivo</Label>
            <Textarea
              id="manual-status-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explica por qué se cambia el estado (quedará en el historial)"
              rows={3}
            />
          </div>

          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Un cambio manual no ejecuta los efectos del flujo: no emite
              factura, no genera tarea de picking ni asigna transportista. Úsalo
              solo para corregir el estado.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!newStatus || !reason.trim() || submitting}
          >
            {submitting ? "Cambiando..." : "Cambiar estado"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualStatusChangeDialog;
